<script>
    /**
     * This program is free software; you can redistribute it and/or
     * modify it under the terms of the GNU General Public License
     * as published by the Free Software Foundation; under version 2
     * of the License (non-upgradable).
     *
     * This program is distributed in the hope that it will be useful,
     * but WITHOUT ANY WARRANTY; without even the implied warranty of
     * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
     * GNU General Public License for more details.
     *
     * You should have received a copy of the GNU General Public License
     * along with this program; if not, write to the Free Software
     * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
     *
     * Copyright (c) 2020-2024 Open Assessment Technologies SA;
     */
    import { createEventDispatcher, onDestroy, onMount } from 'svelte';
    import { __, generateElementId, getActualKey, Validity, ResizeObserver } from '@oat-sa-private/ui-core';
    import { cloneDeep } from 'lodash';
    import historyManagerFactory from './plugins/persistentUndoRedo/historyManagerFactory.js';

    const resizableTypes = {
        none: 'none',
        vertical: 'vertical'
    };

    // native HTML props
    export let disabled = false;
    export let cols;
    export let maxlength;
    export let minlength;
    export let name = __('Text editor');
    export let placeholder;
    export let readonly = false;
    export let required = false;
    export let rows = 7;
    export let minRows;

    // custom props
    export let ariaLabel = __('Text editor');
    export let id = generateElementId('texteditor');
    export let value = '';
    export let resizable = resizableTypes.none;
    export let visited = false;
    export let messages = {
        watchdogError: __('Sorry, this action is not supported in the text editor.')
    };

    /**
     * @typedef EditorConfig
     * @see https://ckeditor.com/docs/ckeditor5/latest/builds/guides/integration/configuration.html
     * Will be applied on top of the defaults defined in ckeditor.js
     * Some properties work subtractively!
     *
     * @property {Function[]} extraPlugins - list of plugins to be added
     * @property {String[]} removePlugins - list of plugin names to remove
     * @property {Object} toolbar
     * @property {String[]} toolbar.items - replaces default items
     * @property {String[]} toolbar.removeItems - list of toolbar item names to remove
     * @property {Boolean} toolbar.shouldNotGroupWhenFull - controls toolbar wrapping
     * @property {String} language
     * @property {Object} [anyPluginName] - provide config object for any enabled plugin
     */
    export let editorConfig = {};

    const dispatch = createEventDispatcher();
    const lockId = Symbol('RichTextEditor');
    const colWidth = 1.5; //rem
    const focusTimeout = 150; // Wait till browser set correct focus element in document.activeElement after blur and before focus
    const validity = new Validity();

    const isHeightFixed = Number.isInteger(rows) && rows > 0; //'false' means ckeditor will automatically expand/collapse to fit its content
    resizable = resizableTypes[resizable] ? resizable : resizableTypes.none;
    let resizedContentHeight;

    let editorInstance;
    let editorElement;
    let initialized = false;
    let invalid = false;
    let blurring = false;
    let ckDomElement;
    let resizeObserver;
    let watchdog;
    let lastGoodData;

    // Persistent Undo/Redo history
    // Picking between default and persistent Undo/Redo plugins
    export let usePersistentUndoRedo = false;
    export let storedEditorHistory = [];
    export let historyLimit;

    const historyManager = usePersistentUndoRedo
        ? historyManagerFactory(storedEditorHistory, history => dispatch('historyUpdate', history), historyLimit)
        : null;

    $: if (editorInstance) {
        if (value !== editorInstance.getData()) {
            editorInstance.setData(value);
        }

        const newReadOnly = readonly || disabled;
        if (editorInstance.isReadOnly !== newReadOnly) {
            if (newReadOnly) {
                editorInstance.enableReadOnlyMode(lockId);
            } else {
                editorInstance.disableReadOnlyMode(lockId);
            }
        }
    }

    $: validity.setRequired(required);

    let customCSSProperties;
    $: {
        customCSSProperties = '';

        if (resizedContentHeight) {
            customCSSProperties += `--content-height: ${resizedContentHeight};`;
        } else if (isHeightFixed) {
            customCSSProperties += `--content-height: calc(4rem + ${rows} * var(--row-height));`;
        }

        if (minRows || isHeightFixed) {
            customCSSProperties += `--min-height: calc(4rem + ${minRows || 1} * var(--row-height))`;
        }
    }

    /**
     * Add/remove placeholder depends on editor content
     *
     * @param {CKEditor} editor
     */
    function handlePlaceholder(editor) {
        const data = editor.getData();
        const rootElement = editor.editing.view.document.getRoot();

        if (!data.length) {
            const placeholderText = editor.sourceElement.getAttribute('placeholder') || '';

            editor.editing.view.change(writer => writer.setAttribute('aria-label', placeholderText, rootElement));
        } else {
            editor.editing.view.change(writer => writer.removeAttribute('aria-label', rootElement));
        }
    }

    /**
     * Calculate that focus fired by pressing Tab button and fire custom event
     * @param {Event} event
     */
    function handleKeydown(event) {
        blurring = false;
        setTimeout(() => {
            if (ckDomElement && getActualKey(event) === 'tab' && document.activeElement === ckDomElement && !blurring) {
                ckDomElement.dispatchEvent(new CustomEvent('tabfocus'));
            }
        }, focusTimeout);
    }

    /**
     * If resizable, start observing ckeditor content element.
     * Browser will append `style="height: [n]px"` when element with `resize:vertical` style is resized by user,
     * but ckeditor will override this style on each re-render (focus/blur/input),
     * so we need to maintain this style ourselves.
     * @param {HTMLElement} contentElement
     */
    function attachResizeObserver(contentElement) {
        if (resizable !== resizableTypes.none && !resizeObserver) {
            resizeObserver = new ResizeObserver(([entry]) => {
                //it's safer not to use 'getBoundingClientRect' etc, because mistake even by 1px will create an endless resize loop
                if (entry.target.style.height) {
                    resizedContentHeight = entry.target.style.height;
                }
            });
            resizeObserver.observe(contentElement);
        }
    }

    /**
     * Adds accessibility attributes to Editor Toolbar's HTML
     * @param {Object} toolbar - from editor.ui.view
     */
    function decorateToolbar(toolbar) {
        toolbar.element.setAttribute('aria-label', ariaLabel);

        // toolbar items can be Buttons, Dropdowns or Separators
        const isButton = item => item.type === 'button';
        const isDropdown = item => item.buttonView && item.buttonView.type === 'button';

        const controls = toolbar.items.filter(item => isButton(item) || isDropdown(item));

        // make first control in toolbar keyboard-focusable
        // (built-in arrow key handlers allow to reach other controls)
        if (controls.length) {
            const firstControl = isButton(controls[0]) ? controls[0] : controls[0].buttonView;
            firstControl.element.setAttribute('tabindex', '0');
        }

        // add custom attributes to Dropdown buttons
        controls
            .filter(item => isDropdown(item))
            .forEach(dropdown => {
                dropdown.element.setAttribute('aria-haspopup', 'true');
                dropdown.element.removeAttribute('aria-pressed');
            });

        // show tooltip
        toolbar.element.addEventListener('focusin', ({ target }) => {
            if (target.classList.contains('ck-button') && target.classList.contains('focus-visible')) {
                const tooltip = target.getElementsByClassName('ck-tooltip')[0];
                if (tooltip) {
                    tooltip.style.opacity = '1';
                    tooltip.style.visibility = 'visible';
                    tooltip.style.display = 'block';
                }
            }
        });
        // hide tooltip
        toolbar.element.addEventListener('focusout', ({ target }) => {
            if (target.classList.contains('ck-button')) {
                const tooltip = target.getElementsByClassName('ck-tooltip')[0];
                if (tooltip) {
                    tooltip.style.opacity = '0';
                    tooltip.style.visibility = 'hidden';
                }
            }
        });
    }

    /**
     * @typedef {Object} ValidityState
     * @property {Boolean} patternMismatch
     * @property {Boolean} valueMissing
     * @property {Boolean} tooLong
     * @property {Boolean} tooShort
     * @property {Boolean} valid - overall aggregation of other props
     */
    /**
     * Calculate the validity of editor data based on defined constraints
     * @param {String} data - editor data (HTML)
     * @param {Number} visibleCharacters
     * @returns {ValidityState}
     */
    function getDataValidity(data, visibleCharacters = 0) {
        const simpleValidity = validity.get(data);
        const enhancedValidity = {
            patternMismatch: simpleValidity.patternMismatch,
            valueMissing: simpleValidity.valueMissing,
            // `tooLong` and `tooShort` must be recalculated based on visible characters, and not HTML value length
            tooLong: maxlength && visibleCharacters > maxlength,
            tooShort: minlength && visibleCharacters < minlength
        };
        return {
            ...enhancedValidity,
            valid: !Object.values(enhancedValidity).some(Boolean)
        };
    }

    let dispatchTimeouts = {};
    /**
     * Dispatch an event, but don't let it re-occur in quick succession
     * @param {String} eventName
     * @param {*} eventDetail
     * @param {Number} delay
     */
    function throttledDispatch(eventName, eventDetail, delay = 2000) {
        if (!dispatchTimeouts[eventName]) {
            dispatchTimeouts[eventName] = setTimeout(() => {
                delete dispatchTimeouts[eventName];
            }, delay);

            dispatch(eventName, eventDetail);
        }
    }

    /**
     * Called when component mounts
     */
    onMount(() => {
        import('./ckeditor.js')
            .then(({ EditorWatchdog, ClassicEditor, HtmlDataProcessor }) => {
                // Watchdog wraps the ClassicEditor and handles errors
                watchdog = new EditorWatchdog(ClassicEditor, { crashNumberLimit: 20 });
                watchdog.on('stateChange', () => {
                    if (watchdog.state === 'crashedPermanently') {
                        watchdog.editor.enableReadOnlyMode('crashed-editor');
                        dispatch('error', { error: new Error('CKEditor has crashed.') });
                    }
                });
                watchdog.on('restart', () => {
                    // the error was recovered, a notification is warranted
                    const notification = {
                        message: messages && messages.watchdogError,
                        hierarchy: 'warning',
                        closeable: true
                    };
                    throttledDispatch('notify', [notification, 'persistent']);
                    if (lastGoodData) {
                        watchdog.editor.setData(lastGoodData);
                    }
                });
                /* eslint-disable-next-line arrow-body-style */
                watchdog.setCreator((element, config) => {
                    // prettier-ignore
                    return ClassicEditor
                        .create(element, { ...config })
                        .then(editor => {
                            // special handling for plugin errors
                            editor.on('mathboxerror', (eventInfo, error) => {
                                // delegate to Watchdog
                                throw error;
                            });

                            const viewElement = editor.editing.view.document.getRoot(); // Allow interacting with CKEditor API
                            ckDomElement = editor.editing.view.getDomRoot(); // DOM element used to rendering content

                            decorateToolbar(editor.ui.view.toolbar);
                            attachResizeObserver(editor.ui.view.editable.element);

                            const wcPlugin = editor.plugins.get('WordCount');

                            // set initial `invalid` to have correct UI state on every load/reload/refresh
                            // this will be repeated on `change:data` events and on editor blur
                            let data = editor.getData();
                            let dataValidity = getDataValidity(data, wcPlugin.characters || data.length);
                            if (visited) {
                                invalid = !dataValidity.valid;
                            }
                            lastGoodData = data;

                            if (usePersistentUndoRedo) {
                                for (let i=0; i < storedEditorHistory.length; i++) {
                                    const historyChunk = storedEditorHistory[i];
                                    //`isUndoable: i > 0` - to not return to initial value set in `<textarea bind:value>`
                                    editor.data.set(historyChunk?.base?.string || '', { batchType: { isUndoable: i > 0 } });
                                }

                                if (storedEditorHistory.length) {
                                    const latestHistoryEntry = storedEditorHistory[storedEditorHistory.length - 1]?.base?.string || '';
                                    if (latestHistoryEntry !== (data || '')) {
                                        editor.data.set(data, { batchType: { isUndoable: true } });
                                    }
                                }
                            }

                            //start sending 'change' events only after `usePersistentUndoRedo` history is restored!
                            editor.model.document.on('change:data', () => {
                                data = editor.getData();
                                dataValidity = getDataValidity(data, wcPlugin.characters || data.length);
                                if (value !== data || (visited && data.length === 0)) {
                                    dispatch('validation', dataValidity);
                                    dispatch('change', {
                                        id,
                                        value: data,
                                        count: {
                                            words: wcPlugin.words,
                                            chars: wcPlugin.characters
                                        }
                                    });
                                }

                                historyManager?.processInputForHistory(data);

                                if (visited) {
                                    invalid = !dataValidity.valid;
                                    handlePlaceholder(editor);
                                }

                                if (watchdog.state === 'ready') {
                                    lastGoodData = data;
                                }
                            });
                            editor.editing.view.document.on('focus', event => {
                                dispatch('focus', { event });
                            });
                            editor.editing.view.document.on('blur', event => {
                                // first blur should validate editor on empty value
                                if (!visited) {
                                    data = editor.getData();
                                    dataValidity = getDataValidity(data, wcPlugin.characters || data.length);

                                    dispatch('validation', dataValidity);
                                    invalid = !dataValidity.valid;
                                }
                                visited = true;
                                blurring = true;
                                editor.editing.view.change(writer => writer.removeClass('key-focus-visible', viewElement));
                                dispatch('blur', { event });
                            });
                            /**
                             * Trigger CKEditor paste event on textarea and make it cancelable
                             */
                            editor.editing.view.document.on('clipboardInput', (event, eventData) => {
                                if (eventData.method === 'paste') {
                                    const originalClipboardData = eventData.dataTransfer._native;
                                    const htmlContent = originalClipboardData.getData('text/html');
                                    const content = htmlContent || eventData.dataTransfer._native.getData('text/plain');
                                    const pasteEvent = new ClipboardEvent('paste', {
                                        clipboardData: originalClipboardData,
                                        /* not standard attributes for firefox */
                                        data: content,
                                        dataType: htmlContent ? 'text/html' : 'text/plain',
                                        /* ----------------------------------  */
                                        cancelable: true,
                                        bubbles: true
                                    });
                                    editorElement.dispatchEvent(pasteEvent);

                                    if (pasteEvent.defaultPrevented) {
                                        event.stop();
                                    }

                                    const selection = editor.model.document.selection;
                                    const view = editor.data.toView(editor.model.getSelectedContent(selection));
                                    const htmlDataProcessor = new HtmlDataProcessor(view);
                                    dispatch('clipboard', {
                                        domEventType: 'paste',
                                        target: editorElement,
                                        content,
                                        replacedContent: htmlDataProcessor.toData(view),
                                        position: selection.getFirstPosition().path
                                    });
                                }
                            });

                            editor.editing.view.document.on('dragstart', (event, eventData) => {
                                dispatch('dragdrop', {
                                    domEventType: 'dragstart',
                                    content: eventData.dataTransfer.getData('text/html'),
                                    position: editor.model.document.selection.getFirstPosition().path
                                });
                            });

                            /**
                             * Trigger CKEditor drop event on textarea and make it cancelable
                             */
                            editor.editing.view.document.on('drop', (event, eventData) => {
                                const dropEvent = new DragEvent('drop', { cancelable: true, bubbles: true });
                                editorElement.dispatchEvent(dropEvent);

                                if (dropEvent.defaultPrevented) {
                                    event.stop();
                                }

                                const content =
                                    eventData.dataTransfer.getData('text/html') ||
                                    eventData.dataTransfer.getData('text/plain');
                                dispatch('dragdrop', {
                                    domEventType: 'drop',
                                    content
                                });
                            });
                            editor.editing.view.document.on('clipboardOutput', (event, eventData) => {
                                const domEventType = eventData.method;
                                if (['copy', 'cut'].includes(domEventType)) {
                                    const htmlDataProcessor = new HtmlDataProcessor(eventData.content);
                                    dispatch('clipboard', {
                                        domEventType,
                                        target: editorElement,
                                        content: htmlDataProcessor.toData(eventData.content),
                                        position: editor.model.document.selection.getFirstPosition().path
                                    });
                                }
                            });
                            // set dotted border around editable content area
                            ckDomElement.addEventListener('tabfocus', () => {
                                editor.editing.view.change(writer => writer.addClass('key-focus-visible', viewElement));
                            });
                            if (Number.isInteger(cols) && cols > 0) {
                                editor.ui.element.style.width = `${cols * colWidth}rem`;
                            }
                            handlePlaceholder(editor);

                            editorInstance = editor;
                            initialized = true;
                            throttledDispatch('ready', {
                                count: {
                                    words: wcPlugin.words,
                                    chars: wcPlugin.characters
                                }
                            });
                            // editor instance must be returned for Watchdog to use
                            return editor;
                        });
                });
                /* eslint-disable-next-line arrow-body-style */
                watchdog.setDestructor(editor => {
                    let result = true;
                    return editor
                        .destroy()
                        .catch(() => {
                            result = false;
                        })
                        .finally(() => {
                            dispatch('destroy', { result });
                        });
                });
                return watchdog.create(editorElement, cloneDeep(editorConfig));
            })
            // catches unrecoverable errors, during editor creation or within Watchdog management
            .catch(error => {
                dispatch('error', { error });
            });
    });

    /**
     * Called before component destroyed
     */
    onDestroy(() => {
        if (resizeObserver) {
            resizeObserver.disconnect();
        }
        if (watchdog) {
            watchdog.destroy(); // takes care of destroying editorInstance as well
        }
        if (usePersistentUndoRedo) {
            historyManager?.destroy();
        }
    });
</script>

<style>
    .cke-wrapper.initialized {
        --ck-spacing-large: 2rem;
        --ck-spacing-standard: 2.5rem;
        --ck-color-text: var(--color-text-default);
        --ck-inner-shadow: auto;
        --ck-focus-ring: none;

        /* convert CKE's own font sizes to a11y-responsive ones */
        --ck-font-face: var(--font-ui);
        --ck-font-size-base: calc(0.8125 * var(--fontsize-body));

        /* make toolbar and its components a11y-responsive */
        --button-container-size: 6rem;
        --button-dropdown-size: 5.75rem;
        --button-size: 5.5rem;
        --ck-list-style-button-size: var(--button-size);
        --ck-character-grid-tile-size: 3rem;
        --ck-color-grid-tile-size: 3rem;
        --ck-resizer-size: 1.25rem;
        --ck-form-header-height: 4.75rem;

        --color-bg-selection: var(--color-gs-light-secondary);
        --color-toolbar-inner-border: var(--color-gs-dark-secondary);

        --ck-color-base-background: var(--color-gs-light);
        --ck-color-list-background: var(--ck-color-base-background);
        --ck-color-dropdown-panel-background: var(--ck-color-base-background);
        --ck-color-dropdown-hover-background: var(--color-bg-input-hover);
        --ck-color-list-button-on-background: var(--color-bg-input-hover);
        --ck-color-list-button-on-text: var(--ck-color-text);
        --ck-color-list-button-hover-background: var(--color-bg-input-hover);
        --ck-color-button-on-hover-background: var(--color-bg-selected);
        --ck-color-button-default-hover-background: var(--color-bg-input-hover);
        --ck-color-button-on-background: var(--color-bg-selection);
        --ck-color-focus-border: var(--color-border-focus);
        --ck-color-focus-outer-shadow: var(--color-brand);
        --ck-color-split-button-hover-background: var(--color-brand-light);
        --ck-color-split-button-hover-border: var(--color-brand-hover);
        /* to apply CKE's default #fafafa toolbar shade over white (or any other) inherited background: */
        --ck-color-toolbar-background: rgba(0, 0, 0, 0.02);

        --min-height: auto;
        --content-height: auto;
        --row-height: calc(var(--line-height-default) * 1em);
        --upload-progress-percents-str: '0%';

        @define-mixin focus-visible-button {
            /* use pseudo-element version of outline-focus, because more consistent across toolbar items with different structures */
            @add-mixin outline-focus-after 0.25rem;
            box-shadow: none;
            background-color: var(--color-gs-light-secondary);
            color: var(--ck-color-text);
        }

        /* editor toolbar */
        & :global(.ck-editor__top .ck.ck-toolbar) {
            z-index: calc(var(--ck-z-default) + 2); /* override --ck-z-modal */
        }
        & :global-nested(.ck.ck-toolbar) {
            border-left: var(--border-medium) solid var(--color-border-default);
            border-top: var(--border-medium) solid var(--color-border-default);
            border-right: var(--border-medium) solid var(--color-border-default);
            padding: 0;
            box-sizing: border-box;
            position: relative;

            /* toolbar buttons reset */
            & .ck-button {
                border-radius: 0;
                border-width: 0;
                transition: none;
                font-family: inherit;
                font-size: inherit;
                letter-spacing: inherit;
                word-spacing: inherit;

                &.ck-on {
                    color: var(--color-text-active);
                    background-color: var(--color-bg-active);
                }

                &:hover {
                    cursor: pointer;

                    &:not(.ck-disabled) {
                        background-color: var(--ck-color-button-default-hover-background);
                        color: var(--ck-color-text);

                        &.ck-on {
                            background-color: var(--ck-color-button-on-hover-background);
                            color: var(--color-text-active);
                        }
                    }
                }
                &:focus-visible {
                    @add-mixin focus-visible-button;
                }
                &[data-focus-visible-added] {
                    @add-mixin focus-visible-button;
                }
                &:active {
                    box-shadow: none;
                }
            }

            & > .ck-toolbar__items {
                & .ck.ck-tooltip__text {
                    width: max-content;
                    max-width: calc(7 * var(--fontsize-body)); /* simple fix for cutting wide tooltips*/
                    white-space: normal;
                    text-align: center;
                }

                & > * {
                    margin: 0;
                    height: var(--button-container-size);
                    @add-mixin flex-center-center;
                    border-bottom: var(--border-medium) solid var(--color-toolbar-inner-border);
                }
                & > .ck.ck {
                    /* override ckeditor's style for [dir='rtl']  */
                    margin-left: 0;
                }
                /* non-separators */
                /* by design, non-separators may grow wider and separators may not */
                & > :not(.ck-toolbar__separator) {
                    flex-basis: auto;
                    min-width: var(--button-container-size);
                    flex-grow: 1;

                    /* top-level toolbar buttons */
                    &.ck-button {
                        padding: 0.5rem;

                        &:hover:not(.ck-disabled) {
                            background-color: var(--ck-color-list-button-on-background);
                            color: var(--ck-color-list-button-on-text);

                            &.ck-on {
                                background-color: var(--ck-color-button-on-hover-background);
                                color: var(--color-text-active);
                            }
                        }
                    }
                    /* top-level toolbar dropdowns & splitbuttons */
                    &.ck-dropdown {
                        & > .ck-dropdown__button:not(.ck-splitbutton) {
                            height: var(--button-dropdown-size);
                            padding: 0 1rem;

                            &:hover:not(.ck-disabled) {
                                background-color: var(--ck-color-dropdown-hover-background);
                                color: var(--ck-color-list-button-on-text);
                            }
                        }
                        & > .ck-splitbutton {
                            & > .ck-button {
                                height: var(--button-dropdown-size);
                            }
                            & > .ck-splitbutton__arrow > .ck-button__icon {
                                margin: 0 0.25rem;
                            }
                        }
                        /* dropdown list items */
                        & .ck-list__item .ck-button.ck-on {
                            &:focus-visible {
                                color: var(--color-text-active);
                                background-color: var(--color-bg-active);
                                &::after {
                                    border-color: var(--color-border-focus-inverted);
                                }
                            }
                        }
                        /* nested dropdown menu */
                        & .ck-toolbar {
                            border-bottom: var(--border-medium) solid var(--color-toolbar-inner-border);

                            & .ck-button {
                                border: 0;
                            }
                        }
                        /* text style dropdown menu */
                        & .ck-heading_heading1 {
                            font-size: var(--fontsize-heading-l);
                        }
                        & .ck-heading_heading2 {
                            font-size: var(--fontsize-heading);
                        }
                        /* special characters dropdown menu */
                        & .ck-character-grid {
                            width: 43.75rem;
                        }
                    }
                    /* accounts for different line position of final pseudo-separator */
                    &:last-child {
                        padding-right: 1rem;
                    }
                }
                /* image upload button is wrapped in an additional container */
                & > .ck-file-dialog-button > .ck-button {
                    width: 100%;
                    height: 100%;
                }
                /* separators */
                & > .ck-toolbar__separator {
                    flex-basis: 1rem;
                    background: none;
                    position: relative;

                    /* Pseudo-element used for the vertical part of separator only */
                    &::after {
                        content: '';
                        position: absolute;
                        width: 0.125rem;
                        top: 0;
                        bottom: 0;
                        left: 50%;
                        border-left: var(--border-thin) solid var(--color-toolbar-inner-border);
                    }
                    /* hide sibling or final separator */
                    & + .ck-toolbar__separator,
                    &:last-child {
                        flex-basis: 0;
                        &::after {
                            content: none;
                        }
                    }
                }
                /* add final growing item to force alignment */
                &::after {
                    content: '';
                    height: var(--button-container-size);
                    flex-grow: 50;
                    border-left: var(--border-thin) solid var(--color-toolbar-inner-border);
                    border-bottom: var(--border-medium) solid var(--color-toolbar-inner-border);
                }
            }
        }

        /* content area styles */
        & :global(.ck.ck-content) {
            color: inherit;
            background: inherit;
            border: var(--border-medium) solid var(--color-border-default);
            border-top: 0;
            font-size: calc(var(--fontsize-body) * 1.25);
            line-height: var(--line-height-default);
            box-sizing: content-box;
            z-index: 1;
            position: relative;
            height: var(--content-height);
            min-height: var(--min-height);
            cursor: initial; /* revert 'html {cursor:default}' global style to get text edit cursor and, in Safari, resize handle cursor  */
        }
        &.resize-vertical :global(.ck.ck-content) {
            resize: vertical;
        }
        &:not(.invalid) :global(.ck.ck-editor:hover .ck-content),
        & :global(.ck.ck-content.ck-focused) {
            border-color: var(--color-border-actionable-hover);
        }
        & :global-nested(.ck.ck-content.key-focus-visible) {
            @add-mixin simple-outline;
            border-color: var(--color-border-selected);
            outline-offset: -0.5rem;
        }
        & :global(.ck.ck-icon) {
            /* CKE styles include will-change: transform, which creates new a compositor layer for every icon, causing slowdown */
            will-change: auto;
        }
        &.invalid {
            & :global(.ck.ck-content) {
                border-color: var(--color-alert);
            }
        }
        & :global-nested(.ck.ck-content) {
            & p:not(:first-child) {
                margin-top: 0;
            }
            & p:not(:last-child) {
                margin-bottom: 0;
            }
        }
        /**
         * Fix for known issue with unwanted sticky toolbar when focused in overflowed container
         * @see https://github.com/ckeditor/ckeditor5/issues/5465
         */
        & :global-nested(.ck.ck-sticky-panel) {
            & .ck.ck-sticky-panel__placeholder {
                height: 0 !important;
            }
            & .ck-sticky-panel__content_sticky {
                position: relative;
                box-shadow: none;
                z-index: auto;
            }
        }
        /* end of fix */

        /* UploadAdapterPlugin: progress bar and overlay for uploading image */
        & :global-nested(.ck.ck-content .image.ck-appear),
        & :global-nested(.ck.ck-content .image-inline.ck-appear) {
            --ck-color-upload-bar-background: var(--color-brand);

            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background: hsla(0, 0%, 12%, 70%);
                border: 2rem solid transparent;
                box-shadow: 0 2rem 0 var(--color-gs-dark) inset;
                box-sizing: border-box;
            }
            &::after {
                content: var(--upload-progress-percents-str);
                position: absolute;
                top: 2rem;
                left: 2rem;
                right: 2rem;
                line-height: 2rem;
                font-size: 1.5rem;
                text-align: center;
                display: block;
                color: var(--color-text-inverted);
                white-space: nowrap;
            }
            & .ck-progress-bar {
                height: 6rem;
                border: 2rem solid transparent;
                background: none;
                box-sizing: border-box;
                box-shadow: 0 2rem 0 var(--color-brand) inset;
            }
        }
        /* end of UploadAdapterPlugin styles */
    }
    @define-mixin readonly {
        color: var(--color-text-disabled);
        & :global(.ck.ck-toolbar) {
            border-color: var(--color-border-disabled);
        }
        & :global(.ck.ck-content) {
            border-color: var(--color-border-disabled);
        }
        & :global(.ck.ck-content.key-focus-visible) {
            border-color: var(--color-border-disabled);
        }
    }
    .readonly {
        pointer-events: none;
        @add-mixin readonly;
    }
    .disabled {
        user-select: none;
        @add-mixin readonly;
        & :global(.ck.ck-content) {
            background: var(--color-bg-disabled-subtle);
        }
        &:hover {
            cursor: not-allowed;
        }
        & :global(.ck.ck-editor) {
            pointer-events: none;
        }
    }

    /* Right-to-left overrides - internal dir attribute (when CKE locale *is* RTL) */
    .cke-wrapper.initialized :global-nested([dir='rtl'] .ck.ck-toolbar) {
        & > .ck.ck-toolbar__items {
            &::after {
                border-left: 0;
                border-right: var(--border-thin) solid var(--color-toolbar-inner-border);
            }
            & > :not(.ck-toolbar__separator) {
                &:last-child {
                    padding-left: 1rem;
                    padding-right: 0.5rem;
                }
            }
        }
    }
</style>

<svelte:window on:keydown={handleKeydown} />
<div
    class="cke-wrapper resize-{resizable}"
    class:initialized
    class:readonly
    class:disabled
    class:required
    class:invalid
    style={customCSSProperties}
    aria-label={ariaLabel}
    aria-required={required ? true : void 0}>
    <textarea
        {id}
        bind:value
        class:hidden={initialized}
        bind:this={editorElement}
        {disabled}
        {cols}
        {maxlength}
        {minlength}
        {name}
        {placeholder}
        {readonly}
        {required}
        {rows}
        on:focus
        on:blur />
</div>
